import { ref, type Ref } from "vue";
import * as Cesium from "cesium";

export function useRotationSymbol(viewer: Ref<Cesium.Viewer | null>) {
  const rotationCenterEntity = ref<Cesium.Entity | null>(null);
  const isRotating = ref<boolean>(false);
  const rotationCenterPosition = ref<Cesium.Cartesian3 | null>(null);
  let handler: Cesium.ScreenSpaceEventHandler | null = null;

  const createRotationSymbol = (): string => {
    const svg = `
      <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
        <circle cx="16" cy="16" r="14" fill="none" stroke="#00ff00" stroke-width="2" opacity="0.8"/>
        <circle cx="16" cy="16" r="2" fill="#00ff00" opacity="0.9"/>
        <path d="M 16 2 A 14 14 0 0 1 28 16" stroke="#00ff00" stroke-width="2" fill="none" opacity="0.6"/>
        <path d="M 28 16 A 14 14 0 0 1 16 30" stroke="#00ff00" stroke-width="2" fill="none" opacity="0.4"/>
        <path d="M 16 30 A 14 14 0 0 1 4 16" stroke="#00ff00" stroke-width="2" fill="none" opacity="0.2"/>
        <path d="M 4 16 A 14 14 0 0 1 16 2" stroke="#00ff00" stroke-width="2" fill="none" opacity="0.1"/>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  const createRotationCenterEntity = (
    position: Cesium.Cartesian3
  ): Cesium.Entity => {
    return viewer.value!.entities.add({
      position: position,
      billboard: {
        image: createRotationSymbol(),
        scale: 1.0,
        verticalOrigin: Cesium.VerticalOrigin.CENTER,
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        scaleByDistance: new Cesium.NearFarScalar(1000, 1.0, 10000, 0.3),
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
      show: true,
    });
  };

  const calculateRotationCenter = (
    mousePosition: Cesium.Cartesian2
  ): Cesium.Cartesian3 | null => {
    if (!viewer.value) return null;

    const camera = viewer.value.camera;
    const scene = viewer.value.scene;

    const pickedPosition = scene.pickPosition(mousePosition);
    if (pickedPosition) {
      return pickedPosition;
    }

    return camera.pickEllipsoid(mousePosition, scene.globe.ellipsoid) || null;
  };

  const hideRotationSymbol = (): void => {
    if (!viewer.value || !rotationCenterEntity.value) return;

    viewer.value.entities.remove(rotationCenterEntity.value);
    rotationCenterEntity.value = null;
    rotationCenterPosition.value = null;
    isRotating.value = false;
  };

  const showRotationSymbol = (position: Cesium.Cartesian3): void => {
    if (!viewer.value) return;

    hideRotationSymbol();
    rotationCenterEntity.value = createRotationCenterEntity(position);
    rotationCenterPosition.value = position;
    isRotating.value = true;
  };

  const setupRotationSymbolHandlers = (): void => {
    if (!viewer.value) return;

    handler = new Cesium.ScreenSpaceEventHandler(viewer.value.canvas);

    handler.setInputAction(
      (event: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
        const rotationCenter = calculateRotationCenter(event.position);
        if (rotationCenter) {
          showRotationSymbol(rotationCenter);
        }
      },
      Cesium.ScreenSpaceEventType.MIDDLE_DOWN
    );

    handler.setInputAction(() => {
      hideRotationSymbol();
    }, Cesium.ScreenSpaceEventType.MIDDLE_UP);

    handler.setInputAction((event: any) => {
      if (isRotating.value && event.button === 1) {
        const rotationCenter = calculateRotationCenter(event.endPosition);
        if (rotationCenter && rotationCenterEntity.value) {
          rotationCenterEntity.value.position = new Cesium.ConstantPositionProperty(
            rotationCenter
          );
          rotationCenterPosition.value = rotationCenter;
        }
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
  };

  const destroyRotationSymbol = (): void => {
    hideRotationSymbol();
    if (handler) {
      handler.destroy();
      handler = null;
    }
  };

  return {
    setupRotationSymbolHandlers,
    destroyRotationSymbol,
    isRotating,
    rotationCenterPosition,
  };
}


